import { useState, useCallback } from 'react';

export interface UseDatabaseOptions {
  onError?: (error: string) => void;
}

export function useDatabase(options: UseDatabaseOptions = {}) {
  const [error, setError] = useState<string | null>(null);

  const handleError = useCallback((err: any, operation: string) => {
    const errorMessage = `Failed to ${operation}`;
    console.error(errorMessage, err);
    setError(errorMessage);
    options.onError?.(errorMessage);
  }, [options]);

  const execute = useCallback(async <T>(
    operation: () => Promise<T>,
    operationName: string
  ): Promise<T | null> => {
    try {
      setError(null);
      return await operation();
    } catch (err) {
      handleError(err, operationName);
      return null;
    }
  }, [handleError]);

  const loadItem = useCallback(async <T>(
    getter: () => Promise<T | undefined>,
    itemType: string = 'item'
  ): Promise<T | null> => {
    return execute(async () => {
      const item = await getter();
      if (!item) {
        throw new Error(`${itemType} not found`);
      }
      return item;
    }, `load ${itemType}`);
  }, [execute]);

  const loadList = useCallback(async <T>(
    getter: () => Promise<T[]>,
    itemType: string = 'items'
  ): Promise<T[]> => {
    const result = await execute(getter, `load ${itemType}`);
    return result || [];
  }, [execute]);

  const saveItem = useCallback(async <T>(
    saver: () => Promise<T>,
    itemType: string = 'item'
  ): Promise<T | null> => {
    return execute(saver, `save ${itemType}`);
  }, [execute]);

  const updateItem = useCallback(async (
    updater: () => Promise<void>,
    itemType: string = 'item'
  ): Promise<boolean> => {
    const result = await execute(updater, `update ${itemType}`);
    return result !== null;
  }, [execute]);

  const deleteItem = useCallback(async (
    deleter: () => Promise<void>,
    itemType: string = 'item'
  ): Promise<boolean> => {
    const result = await execute(deleter, `delete ${itemType}`);
    return result !== null;
  }, [execute]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    error,
    loadItem,
    loadList,
    saveItem,
    updateItem,
    deleteItem,
    clearError,
    execute,
  };
}